import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import type { TCategory } from '../../types'
import type { Context, NextEventParameters } from './types'

// URL parameter handling functions
export const getClientSearchParams = () => {
  if (typeof window !== 'undefined') {
    const search = window?.location?.search
    return new URLSearchParams(search)
  }
  return undefined
}

export const updateQueryParam = (key: string, value: string) => {
  const qp = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  )
  qp.set(key, value)
  const history =
    typeof window !== 'undefined'
      ? window.history
      : {
          replaceState: () => {},
        }
  const query = qp.toString()
  history.replaceState(null, '', query ? `?${query}` : '')
}

export const getCategoryInterestsUrlParam = () => {
  const categories = getClientSearchParams()?.get(
    QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES,
  )
  if (typeof categories === 'string') return categories
  return undefined
}

export const getSubcatInterestsUrlParam = () => {
  const subcats = getClientSearchParams()?.get(
    QUIZ_QUERY_PARAMS.SELECTED_SUBCATEGORIES,
  )
  if (typeof subcats === 'string') return parseCSVToNumberList(subcats)
  return undefined
}

export const parseCSVToNumberList = (csv: string) =>
  csv
    .split(',')
    .map((idStr) => parseInt(idStr, 10))
    .filter((v) => !Number.isNaN(v))

// Helper functions for state management
export const getCategoryInterests = (
  categoryInterestsCSV: string,
  cats: TCategory[],
) => {
  const allCategorySelections = parseCSVToNumberList(categoryInterestsCSV)
  allCategorySelections.sort((aID, bID) => {
    const a = cats.find((c) => c.id === aID)?.name || aID
    const b = cats.find((c) => c.id === bID)?.name || bID
    return a > b ? 1 : -1
  })
  return allCategorySelections
}

export const setsEqual = <T>(xs: Set<T>, ys: Set<T>) =>
  xs.size === ys.size && [...xs].every((x) => ys.has(x))

export const getCategoryInterestIds = (ctx: Context) =>
  getCategoryInterests(ctx.categoryInterestsCSV || '', ctx.allCategories)

export const noSubcatRefinedCategories = (ctx: Context) => {
  // guard to decide "back" functionality on the subcat interests step
  // true => go back to the Overview/Skip page
  // false => go to refine the previously refined subcat
  const allCats = new Set(getCategoryInterestIds(ctx))
  const unrefinedCats = new Set(ctx.catsToRefineSubcats)
  return setsEqual(allCats, unrefinedCats)
}

export const userShouldSkipToMotivationRefinement = (ctx: Context) =>
  typeof ctx.categoryInterestsCSV === 'string' &&
  ctx.categoryInterestsCSV.length >= 1 &&
  ctx.userHasSkipQ1Param

export const userShouldSkipToSubcatInterests = (ctx: Context) =>
  userShouldSkipToMotivationRefinement(ctx) &&
  ctx.userHasSkipMotivationRefinementParam

export const allCategoriesHaveBeenSubcatRefined = (
  ctx: Context,
  event: {
    type: 'next'
    params?: NextEventParameters
  },
) => {
  const allCatsWithoutEvent = (ctx.catsToRefineSubcats || []).filter(
    (catId) => {
      if (event?.params?.machineEventType === 'subcat_refinement') {
        return catId !== event.params.categoryIdRefined
      }
      return false
    },
  )

  return allCatsWithoutEvent.length === 0
}

// Experiment-specific helper functions
export const shouldShowSpringboardInterstitial = (ctx: Context) =>
  !!ctx.springboardItemType && !!ctx.springboardItem

// start pen-5972_v1_quiz_time_reordering
export const shouldShowTimeQuestionFirstExp = (ctx: Context) =>
  ctx.isTimeQuestionFirstExpVariant

export const userShouldSkipToTimeQuestionFirstExp = (ctx: Context) =>
  shouldShowTimeQuestionFirstExp(ctx) &&
  userShouldSkipToMotivationRefinement(ctx)
// end pen-5972_v1_quiz_time_reordering
